import { jsxs, jsx } from "react/jsx-runtime";
import { memo, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Autocomplete, Typography } from "@mui/material";
import { useField } from "formik";
import { getHasError } from "../../utils/helpers/getHasError.js";
import { useAddressSuggestions } from "../../utils/hooks/useAddressSuggestions.js";
import { useFormHelpers } from "../../utils/hooks/useFormHelpers.js";
import { useSubmitCount } from "../../utils/hooks/useSubmitCount.js";
import { TextField } from "../../utils/shared/TextField.js";
import { TextFieldLabel } from "../../utils/shared/TextFieldLabel.js";
import { combinedFields } from "../../utils/yup/main.js";
import { FieldHelperText } from "../field-helper-text/field-helper-text.js";
import { Translate } from "../translate/translate.js";
import "use-debounce";
import "../../utils/helpers/googlePlaces.js";
import "../../utils/helpers/places.js";
import "yup";
import "../../utils/fields/personalInformation.js";
import "../../../../../type/Appointment.js";
import "../masked-date-input/masked-date-input.js";
import "react-text-mask";
import "../../utils/entities/Form.js";
import "../../../ana-toolbox/ana-toolbox.js";
import "../../../../../constants/date/date.js";
import "date-fns/fp/format";
import "date-fns/isValid";
import "date-fns/parse";
import "date-fns";
import "../../utils/helpers/toBoolean.js";
import "../../utils/helpers/createFields.js";
import "../../utils/helpers/resetFields.js";
import "../../utils/yup/yup.js";
import "../../../../../utils/pipe.js";
import "date-fns/fp";
import "libphonenumber-js/max";
import "libphonenumber-js/mobile";
import "../../utils/helpers/predicates.js";
import "../../utils/yup/emailValidation.js";
import "../../utils/fields/contactDetails.js";
import "../../utils/helpers/featureFlags.js";
import "../../utils/helpers/getAgeFromBirthday.js";
import "../../utils/yup/buildValidationSchema.js";
import "../../utils/fields/miscellaneous.js";
import "../../utils/fields/insurance.js";
import "../../utils/fields/allergiesAndMedications.js";
import "../../../../../type/Disease.js";
import "../../utils/config/medicaments.js";
import "../../utils/fields/wishesAndExpectations.js";
import "../../utils/fields/consents.js";
import "../../utils/fields/profileInterest.js";
const AddressFieldBase = memo(
  ({
    name,
    value: externalValue,
    error,
    label,
    placeholder,
    placesEnabled = false,
    required,
    helperText,
    onBlur,
    onChange,
    onReceiveAddress
  }) => {
    const { t } = useTranslation();
    const [divRef, setDivRef] = useState(null);
    const { value, options, handleInputChange, handleChange } = useAddressSuggestions({
      name,
      containerElement: divRef,
      value: externalValue,
      onChange,
      onReceiveAddress
    });
    const getOptionLabel = (option) => {
      return typeof option === "string" ? option : option.description;
    };
    const placesServiceContainer = /* @__PURE__ */ jsx("div", { ref: setDivRef });
    const renderOption = (props, option) => {
      return /* @__PURE__ */ jsxs(Box, { ...props, children: [
        /* @__PURE__ */ jsx(Typography, { children: option.structured_formatting.main_text }),
        option.structured_formatting.secondary_text && /* @__PURE__ */ jsx(Typography, { children: option.structured_formatting.secondary_text })
      ] });
    };
    const renderInput = (props) => /* @__PURE__ */ jsx(
      TextField,
      {
        ...props,
        error: Boolean(error),
        name,
        placeholder: placeholder ? t(placeholder) || "" : t(`${label}_placeholder`, {
          defaultValue: "",
          postProcess: []
        }) || "",
        InputLabelProps: {
          id: `${name}-label`
        },
        InputProps: {
          ...props.InputProps,
          style: {
            padding: "22px 16px 3px"
          },
          className: void 0
        },
        inputProps: {
          ...props.inputProps,
          style: {
            padding: 0
          },
          "aria-required": required,
          "aria-labelledby": `${name}-label`
        },
        label: /* @__PURE__ */ jsx(TextFieldLabel, { required, children: /* @__PURE__ */ jsx(Translate, { children: label }) })
      }
    );
    return /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(
        Autocomplete,
        {
          options,
          inputValue: value,
          getOptionLabel,
          onInputChange: handleInputChange,
          onChange: handleChange,
          renderOption,
          renderInput,
          onBlur,
          autoHighlight: true,
          disableClearable: true,
          disablePortal: true,
          freeSolo: true
        }
      ),
      /* @__PURE__ */ jsx(FieldHelperText, { helperText, error, name }),
      placesEnabled && placesServiceContainer
    ] });
  }
);
const AddressField = (props) => {
  const submitCount = useSubmitCount();
  const formHelpers = useFormHelpers();
  const [field, meta] = useField(props.name);
  const hasError = useMemo(() => getHasError(meta, submitCount ?? 1), [meta, submitCount]);
  const fieldSettings = useMemo(
    () => combinedFields[props.name],
    [props.name]
  );
  const handleChange = useCallback(
    (e) => {
      var _a, _b;
      (_b = (_a = fieldSettings == null ? void 0 : fieldSettings.hooks) == null ? void 0 : _a.beforeChange) == null ? void 0 : _b.call(_a, e.target.value, formHelpers, e);
      field.onChange(e);
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  return /* @__PURE__ */ jsx(AddressFieldBase, { error: hasError ? meta.error : void 0, ...props, ...field, onChange: handleChange });
};
export {
  AddressField
};
